import * as React from "react";

import { ArrowDropDown, ArrowDropUp, Close, Tune } from "@mui/icons-material";
import Button from "@mui/material/Button";

import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";

import { useFilterContext } from "../FilterContext";
import { getFilterConfigByKey } from "../filters";
import { FilterData, FilterFieldKey } from "../typing";

export default function FilterButton(props: {
  fieldId: FilterFieldKey;
  checkedValue: string;
  isAll: boolean;
}) {
  const containerId = React.useRef(`ID_${Math.random() * 100000}`);

  const { isMobile } = usePlatform();

  const {
    setOpen,
    setAnchorEl,
    fieldId,
    setFieldId,
    open,
    resetFilterData,
    resetFilterField,
    filterOptions,
  } = useFilterContext();

  const currentOpen = fieldId !== props.fieldId ? false : open;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isMobile) {
      setFieldId(props.fieldId);
      setOpen(true);
      return;
    }
    setAnchorEl(event.currentTarget);
    setOpen((prevState) => {
      if (fieldId !== props.fieldId) {
        return true;
      }
      return !prevState;
    });
    setFieldId(props.fieldId);
  };

  const handleClear = useEvent((event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    props.isAll ? resetFilterData() : resetFilterField(props.fieldId as keyof FilterData);
  });

  const defaultText = React.useMemo(() => {
    const label = getFilterConfigByKey(props.fieldId)?.label || "";
    if (props.isAll) {
      return "All filters";
    }
    if (props.fieldId === "programs") {
      return `${label} (${filterOptions.programsOptions.length})`;
    } else if (props.fieldId === "airlines") {
      return `${label} (${filterOptions.airlinesOptions.length})`;
    } else if (props.fieldId === "banks") {
      return `${label} (${filterOptions.banksOptions.length})`;
    } else if (props.fieldId === "airportOutbound") {
      return `${label} (${filterOptions.airportOutboundOptions.length})`;
    } else if (props.fieldId === "airportInbound") {
      return `${label} (${filterOptions.airportInboundOptions.length})`;
    } else if (props.fieldId === "airport") {
      return `${label} (${filterOptions.airportOptions.length})`;
    } else if (props.fieldId === "aircrafts") {
      return `${label} (${filterOptions.aircraftsOptions.length})`;
    } else if (props.fieldId === "connectingAirports") {
      return `${label} (${filterOptions.connectingAirportsOptions.length})`;
    } else if (props.fieldId === "cabins") {
      return `${label} (${filterOptions.cabinsOptions.length})`;
    } else {
      return label;
    }
  }, [props.isAll, filterOptions, props.fieldId]);

  const DefineIcon = getFilterConfigByKey(props.fieldId)?.icon;

  return (
    <div id={containerId.current} className="ml-[10px] first:ml-0 mb-[10px]">
      {props.checkedValue ? (
        <Button
          size="small"
          variant={props.isAll ? "text" : "contained"}
          className="whitespace-nowrap"
          onClick={handleClick}
          startIcon={props.isAll ? <Tune /> : DefineIcon ? <DefineIcon /> : null}
          endIcon={<Close onClick={handleClear} />}
          sx={{
            textTransform: "capitalize",
            whiteSpace: "nowrap",
          }}
        >
          {props.checkedValue}
        </Button>
      ) : (
        <Button
          size="small"
          variant={props.isAll ? "text" : "outlined"}
          onClick={handleClick}
          startIcon={props.isAll ? <Tune /> : DefineIcon ? <DefineIcon /> : null}
          endIcon={currentOpen ? <ArrowDropUp /> : <ArrowDropDown />}
          sx={{
            textTransform: "capitalize",
            whiteSpace: "nowrap",
          }}
        >
          {defaultText}
        </Button>
      )}
    </div>
  );
}
